// brandThumbnail — Jun 15.
//
// Pick the best image for a brand tile / sidebar switcher / etc. The
// fallback chain is:
//
//   1. The brand's uploaded logo (BrandRecord.logoUrl, set by the wizard
//      scrape or manual upload). This is the highest-quality option.
//   2. The site's favicon, served via Google's S2 favicon endpoint:
//      https://www.google.com/s2/favicons?domain=<host>&sz=64
//      Stable, fast, no per-domain configuration needed. Returns a small
//      tinted icon for any reachable domain.
//   3. null — callers should render initials in this case.
//
// Why Google S2 instead of scraping `<link rel="icon">` server-side:
//  - Zero backend work (no schema change, no scrape pipeline tweak).
//  - Handles dynamic SPAs that lazy-load icons from JS-injected tags.
//  - Falls through gracefully on dead domains (returns a placeholder
//    icon that still reads as "a tile").
//
// Resolution sz=64 is enough for a 48px tile at 2× density. Bump to 128
// if we ever render larger.

const FAVICON_SIZE = 64;

/** Extract a hostname from a source_url stored on a brand. Returns null
 *  when the input is empty / malformed. */
function hostFor(sourceUrl: string | null | undefined): string | null {
  if (!sourceUrl) return null;
  const trimmed = sourceUrl.trim();
  if (!trimmed) return null;
  try {
    const withScheme = trimmed.startsWith('http')
      ? trimmed
      : `https://${trimmed}`;
    const { hostname } = new URL(withScheme);
    return hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** Public URL for the favicon of a domain via Google's S2 service.
 *  Returns null if the source URL is missing / unparseable. */
export function faviconUrlFor(sourceUrl: string | null | undefined): string | null {
  const host = hostFor(sourceUrl);
  if (!host) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${FAVICON_SIZE}`;
}

/** Ordered list of image URLs to try for a brand tile, best → most reliable:
 *    1. logoUrl        — the scraped/uploaded brand logo (highest quality)
 *    2. faviconUrl     — the brand's OWN scraped <link rel=icon> favicon
 *    3. Google S2      — favicon-by-domain (works for any reachable site)
 *  Deduped + null-filtered. The renderer walks this on each <img> onError so
 *  a broken logo falls through to a favicon instead of collapsing to initials.
 *  Empty array → caller shows initials. */
export function brandThumbnailCandidates(brand: {
  logoUrl?: string | null;
  faviconUrl?: string | null;
  sourceUrl?: string | null;
}): string[] {
  const out: string[] = [];
  const push = (u: string | null | undefined) => {
    if (u && !out.includes(u)) out.push(u);
  };
  push(brand.logoUrl);
  push(brand.faviconUrl);
  push(faviconUrlFor(brand.sourceUrl));
  return out;
}

/** Resolve a brand thumbnail URL with logo > favicon fallback. Returns
 *  null when all are missing — caller should show initials. (Kept for callers
 *  that only need the first choice; prefer brandThumbnailCandidates.) */
export function brandThumbnailUrl(brand: {
  logoUrl?: string | null;
  faviconUrl?: string | null;
  sourceUrl?: string | null;
}): string | null {
  return brandThumbnailCandidates(brand)[0] ?? null;
}

// ---------------------------------------------------------------------------
// Brand monogram avatar — a single-letter tile on the brand's OWN primary
// color, with a black/white letter picked for contrast. Shared by the sidebar
// switcher (BrandThumbnailImg) and the /brands manager cards so both look
// identical. We deliberately DON'T render the scraped logo in a small square:
// brand logos are usually wide wordmarks (e.g. FENTY BEAUTY at 1024×56) that
// crop to an unreadable sliver. A colored monogram is always legible.
// ---------------------------------------------------------------------------

/** Deterministic pleasant color when a brand has no scraped color. */
export function brandColorFromName(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 52% 45%)`;
}

/** Black or white text for legibility on a hex background. Non-hex inputs
 *  (e.g. the hsl fallback above) read fine as white at L=45%. */
export function readableTextOn(bg: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(bg.trim());
  if (!m) return '#ffffff';
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#111111' : '#ffffff';
}

/** {letter, bg, fg} for a brand monogram tile. bg = the brand's own primary
 *  color (colors[0]) or a name-derived fallback; fg = the contrast letter. */
export function brandAvatarStyle(
  brand: { name?: string | null; colors?: string[] | null } | null,
  fallbackInitials?: string,
): { letter: string; bg: string; fg: string } {
  const base = brand?.name || fallbackInitials || 'B';
  const letter = (base.trim().charAt(0) || 'B').toUpperCase();
  const bg = (brand?.colors && brand.colors[0]) || brandColorFromName(base);
  return { letter, bg, fg: readableTextOn(bg) };
}
